import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { BriefcaseIcon, CalendarIcon } from 'lucide-react';
interface ExperienceItem {
  title: string;
  company: string;
  period: string;
  description: string[];
}
export function Experience() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const experiences: ExperienceItem[] = [{
    title: 'Senior Software Engineer',
    company: 'Tech Company',
    period: '2022 - Present',
    description: ['Led development of key features serving 100K+ users', 'Architected scalable microservices infrastructure', 'Mentored junior developers and conducted code reviews']
  }, {
    title: 'Software Engineer',
    company: 'Startup Inc',
    period: '2020 - 2022',
    description: ['Built full-stack web applications using React and Node.js', 'Implemented CI/CD pipelines reducing deployment time by 60%', 'Collaborated with design team to improve user experience']
  }, {
    title: 'Junior Developer',
    company: 'Digital Agency',
    period: '2018 - 2020',
    description: ['Developed responsive websites for various clients', 'Maintained and optimized existing codebases', 'Participated in agile development processes']
  }];
  return <section id="experience" className="relative py-32 px-6 bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 overflow-hidden" data-id="element-48">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-30" data-id="element-49">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full blur-3xl" data-id="element-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-300 to-cyan-300 rounded-full blur-3xl" data-id="element-51" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10" ref={ref} data-id="element-52">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-53">
          <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 mb-4" data-id="element-54">
            Experience
          </h2>
          <div className="h-2 w-32 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full mx-auto" data-id="element-55" />
        </motion.div>

        <div className="relative" data-id="element-56">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 via-pink-400 to-orange-400 rounded-full hidden md:block" data-id="element-57" />

          <div className="space-y-12" data-id="element-58">
            {experiences.map((exp, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -50
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.2
          }} className="relative" data-id="element-59">
                {/* Timeline dot */}
                <div className="absolute left-6 top-8 w-5 h-5 bg-gradient-to-br from-pink-500 to-orange-500 rounded-full border-4 border-white shadow-lg hidden md:block z-10" data-id="element-60" />

                <div className="md:ml-20 group" data-id="element-61">
                  <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-purple-200 overflow-hidden" data-id="element-62">
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-63" />

                    <div className="relative z-10" data-id="element-64">
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4" data-id="element-65">
                        <div className="flex items-start gap-4" data-id="element-66">
                          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl" data-id="element-67">
                            <BriefcaseIcon className="text-white" size={24} data-id="element-68" />
                          </div>
                          <div data-id="element-69">
                            <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-70">
                              {exp.title}
                            </h3>
                            <p className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600" data-id="element-71">
                              {exp.company}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full" data-id="element-72">
                          <CalendarIcon size={16} className="text-purple-600" data-id="element-73" />
                          <span className="text-sm font-bold text-purple-900" data-id="element-74">
                            {exp.period}
                          </span>
                        </div>
                      </div>

                      <ul className="space-y-3 mt-6" data-id="element-75">
                        {exp.description.map((item, i) => <motion.li key={i} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={isInView ? {
                      opacity: 1,
                      x: 0
                    } : {}} transition={{
                      delay: index * 0.2 + i * 0.1
                    }} className="flex items-start gap-3 text-slate-700" data-id="element-76">
                            <span className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full mt-2" data-id="element-77" />
                            <span className="leading-relaxed" data-id="element-78">{item}</span>
                          </motion.li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}